import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Plus, Loader2, Lock, RotateCcw, CheckCircle2, Clock } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { PageHeader } from '@/components/efs/PageHeader';
import { cn } from '@/lib/utils';
import { formatDateTime } from '@/lib/format';
import { useCurrentUser } from '@/lib/auth-context';
import { hasPermission } from '@/lib/permissions';

import {
  useCreateFirstValuation,
  useFinalizeFirstValuation,
  useFirstValuationDetail,
  useFirstValuationList,
  useLockFirstValuation,
  useRequestRevaluation,
  useSubmitValuation,
} from './useFirstValuation';
import {
  creatableFirstValuationModes,
  finalBasisValues,
  finalDiffersFromMiddle,
  ownSubmission,
  resolveFinalValue,
  type CreatableFirstValuationMode,
  type DifferenceBand,
  type FirstValuationDto,
  type SubmissionDto,
} from './types';
import { ValuationPackageTable } from './ValuationPackageTable';
import { fetchValuationPackages } from './api';

const NS = 'firstValuation';

type TFn = (key: string, opts?: Record<string, unknown>) => string;

/** Format a JPY valuation amount (decimal string) for display. */
const formatAmount = (value: string | null | undefined): string => {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return new Intl.NumberFormat('ja-JP', {
    style: 'currency',
    currency: 'JPY',
    maximumFractionDigits: 0,
  }).format(n);
};

/** Format a difference rate fraction (0.10) as a percent string ("10.0%"). */
const formatRate = (value: string | null | undefined): string => {
  if (value == null || value === '') return '—';
  const n = Number(value);
  if (!Number.isFinite(n)) return value;
  return `${(n * 100).toFixed(1)}%`;
};

const bandVariant = (band: DifferenceBand): 'default' | 'secondary' | 'destructive' | 'outline' => {
  switch (band) {
    case 'normal':
      return 'secondary';
    case 'warning':
      return 'outline';
    case 'abnormal':
    case 'revaluation_suggested':
      return 'destructive';
    default:
      return 'outline';
  }
};

/** Name a submission row: the company, falling back to its legacy side code. */
const estimatorName = (submission: SubmissionDto, t: TFn): string =>
  submission.estimator_company_name ??
  (submission.estimator_side
    ? submission.estimator_side.toUpperCase()
    : t('efs.common.messages.empty'));

// ---- submit dialog ---------------------------------------------------------

// A typed amount keeps the 2-decimal rule. The read-only per-unit total is
// whatever the units hold (numeric(14,3)), so it may carry 3 decimals.
const buildSubmitSchema = (t: TFn, unitTotal: boolean) =>
  z.object({
    valuation_amount: z
      .string()
      .min(1, t('efs.firstValuation.validation.amount_required'))
      .regex(
        unitTotal ? /^\d+(\.\d{1,3})?$/ : /^\d+(\.\d{1,2})?$/,
        t('efs.firstValuation.validation.number_invalid'),
      ),
    material_price_reference: z.string().optional(),
  });

// Sum unit amounts in thousandths so 0.1 + 0.2 gives "0.3", not
// "0.30000000000000004". Unit amounts are numeric(14,3), well inside 2^53.
const sumUnitAmounts = (amounts: string[]): string => {
  const thousandths = amounts.reduce((sum, a) => sum + Math.round(Number(a) * 1000), 0);
  const whole = Math.trunc(thousandths / 1000);
  const frac = String(thousandths % 1000)
    .padStart(3, '0')
    .replace(/0+$/, '');
  return frac ? `${whole}.${frac}` : String(whole);
};

type SubmitFormInput = z.infer<ReturnType<typeof buildSubmitSchema>>;

const SubmitDialog = ({
  valuationId,
  open,
  onOpenChange,
}: {
  valuationId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) => {
  const { t } = useTranslation(NS);
  const mutation = useSubmitValuation();

  // The server files the sum of the per-unit rows whenever any row is priced
  // and ignores the typed amount, so show that sum read-only instead of an
  // editable figure that would be silently discarded. Same cache entry as the
  // unit table above.
  const packagesQuery = useQuery({
    queryKey: ['first-valuations', valuationId, 'packages'],
    queryFn: () => fetchValuationPackages(valuationId),
    enabled: open,
  });
  const packageTotal = useMemo(() => {
    const priced = (packagesQuery.data ?? []).flatMap((row) =>
      row.valuation_amount !== null && row.valuation_amount !== undefined
        ? [String(row.valuation_amount)]
        : [],
    );
    if (priced.length === 0) return null;
    return sumUnitAmounts(priced);
  }, [packagesQuery.data]);

  const schema = useMemo(() => buildSubmitSchema(t, packageTotal !== null), [t, packageTotal]);
  const form = useForm<SubmitFormInput>({
    resolver: zodResolver(schema),
    defaultValues: { valuation_amount: '', material_price_reference: '' },
  });
  useEffect(() => {
    if (open && packageTotal !== null) {
      form.setValue('valuation_amount', packageTotal, { shouldValidate: true });
    }
  }, [open, packageTotal, form]);

  const onSubmit = form.handleSubmit(async (values) => {
    const note = values.material_price_reference?.trim();
    await mutation.mutateAsync({
      id: valuationId,
      body: {
        valuation_amount: values.valuation_amount,
        material_price_reference: note ? { note } : undefined,
      },
    });
    form.reset();
    onOpenChange(false);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('efs.firstValuation.submit.title')}</DialogTitle>
          <DialogDescription>{t('efs.firstValuation.submit.subtitle')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <FormField
              control={form.control}
              name="valuation_amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.firstValuation.fields.valuation_amount')}</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      inputMode="decimal"
                      placeholder="0"
                      {...field}
                      readOnly={packageTotal !== null}
                    />
                  </FormControl>
                  <p className="text-xs text-efs-text-muted">
                    {packageTotal !== null
                      ? t('efs.firstValuation.submit.sum_locked_hint')
                      : t('efs.firstValuation.submit.sum_hint')}
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="material_price_reference"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.firstValuation.fields.material_price_reference')}</FormLabel>
                  <FormControl>
                    <Textarea
                      rows={3}
                      placeholder={t(
                        'efs.firstValuation.fields.material_price_reference_placeholder',
                      )}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={mutation.isPending}
              >
                {t('efs.common.actions.cancel', { defaultValue: 'キャンセル' })}
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {mutation.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                {t('efs.firstValuation.actions.submit')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

// ---- finalize dialog -------------------------------------------------------

const buildFinalizeSchema = (t: TFn) =>
  z
    .object({
      final_basis: z.enum(finalBasisValues),
      final_company_id: z.string().optional(),
      final_value: z.string().optional(),
      final_reason: z.string().optional(),
    })
    .refine(
      (v) =>
        v.final_basis !== 'custom' || (!!v.final_value && /^\d+(\.\d{1,2})?$/.test(v.final_value)),
      {
        path: ['final_value'],
        message: t('efs.firstValuation.validation.final_value_required'),
      },
    )
    .refine((v) => v.final_basis !== 'company' || !!v.final_company_id, {
      path: ['final_company_id'],
      message: t('efs.firstValuation.validation.final_company_required'),
    });

type FinalizeFormInput = z.infer<ReturnType<typeof buildFinalizeSchema>>;

const FinalizeDialog = ({
  dto,
  open,
  onOpenChange,
}: {
  dto: FirstValuationDto;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) => {
  const { t } = useTranslation(NS);
  const finalize = useFinalizeFirstValuation();

  // Only a company that actually filed can be adopted — the server enforces
  // this too, but offering the others would just be a dead option.
  const filed = dto.submissions.filter((s) => s.is_submitted && s.estimator_company_id);

  const schema = useMemo(() => buildFinalizeSchema(t), [t]);
  const form = useForm<FinalizeFormInput>({
    resolver: zodResolver(schema),
    defaultValues: {
      final_basis: 'middle',
      final_company_id: undefined,
      final_value: '',
      final_reason: '',
    },
  });

  const basis = form.watch('final_basis');
  const chosenCompany = form.watch('final_company_id');
  const customValue = form.watch('final_value') ?? '';
  const adoptedAmount = filed.find(
    (s) => s.estimator_company_id === chosenCompany,
  )?.valuation_amount;
  const resolved = resolveFinalValue(basis, dto.comparison, customValue, adoptedAmount);
  const reasonRequired = finalDiffersFromMiddle(resolved, dto.comparison);

  const onSubmit = form.handleSubmit(async (values) => {
    // Client-side mirror of the server rule: reason required when the resolved
    // final value diverges from the midpoint. The server also enforces this.
    if (reasonRequired && !values.final_reason?.trim()) {
      form.setError('final_reason', {
        message: t('efs.firstValuation.validation.final_reason_required'),
      });
      return;
    }
    await finalize.mutateAsync({
      id: dto.first_valuation_id,
      body: {
        final_basis: values.final_basis,
        final_company_id: values.final_basis === 'company' ? values.final_company_id : undefined,
        final_value: values.final_basis === 'custom' ? values.final_value?.trim() : undefined,
        final_reason: values.final_reason?.trim() ? values.final_reason.trim() : undefined,
      },
    });
    form.reset();
    onOpenChange(false);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('efs.firstValuation.finalize.title')}</DialogTitle>
          <DialogDescription>{t('efs.firstValuation.finalize.subtitle')}</DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <FormField
              control={form.control}
              name="final_basis"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('efs.firstValuation.fields.final_basis')}</FormLabel>
                  <FormControl>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {finalBasisValues.map((b) => (
                          <SelectItem key={b} value={b}>
                            {t(`efs.firstValuation.basis.${b}`)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {basis === 'company' ? (
              <FormField
                control={form.control}
                name="final_company_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('efs.firstValuation.fields.final_company')}</FormLabel>
                    <FormControl>
                      <Select value={field.value ?? ''} onValueChange={field.onChange}>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {filed.map((s) => (
                            <SelectItem
                              key={s.estimator_company_id!}
                              value={s.estimator_company_id!}
                            >
                              {estimatorName(s, t)}
                              {s.valuation_amount ? ` — ${formatAmount(s.valuation_amount)}` : ''}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}
            {basis === 'custom' ? (
              <FormField
                control={form.control}
                name="final_value"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('efs.firstValuation.fields.final_value')}</FormLabel>
                    <FormControl>
                      <Input type="text" inputMode="decimal" placeholder="0" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            ) : null}
            <div className="rounded-md bg-efs-bg-subtle px-3 py-2 text-sm text-efs-text-muted">
              {t('efs.firstValuation.finalize.resolved_preview', {
                value: formatAmount(resolved),
              })}
            </div>
            <FormField
              control={form.control}
              name="final_reason"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    {t('efs.firstValuation.fields.final_reason')}
                    {reasonRequired ? <span className="ml-1 text-efs-error">*</span> : null}
                  </FormLabel>
                  <FormControl>
                    <Textarea
                      rows={3}
                      placeholder={t('efs.firstValuation.fields.final_reason_placeholder')}
                      {...field}
                    />
                  </FormControl>
                  {reasonRequired ? (
                    <p className="text-xs text-efs-text-muted">
                      {t('efs.firstValuation.finalize.reason_required_hint')}
                    </p>
                  ) : null}
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={finalize.isPending}
              >
                {t('efs.common.actions.cancel', { defaultValue: 'キャンセル' })}
              </Button>
              <Button type="submit" disabled={finalize.isPending}>
                {finalize.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                {t('efs.firstValuation.actions.finalize')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

// ---- create dialog ---------------------------------------------------------

const CreateDialog = ({
  batchId,
  open,
  onOpenChange,
}: {
  batchId: string;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) => {
  const { t } = useTranslation(NS);
  const create = useCreateFirstValuation();
  const [mode, setMode] = useState<CreatableFirstValuationMode>('single_trip');

  const onConfirm = async () => {
    await create.mutateAsync({ batchId, body: { mode } });
    setMode('single_trip');
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        // Clear any stale create error (e.g. a prior weight-difference 409
        // conflict) when the dialog is dismissed so it does not linger on reopen.
        if (!v) create.reset();
        onOpenChange(v);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('efs.firstValuation.create.title')}</DialogTitle>
          <DialogDescription>{t('efs.firstValuation.create.subtitle')}</DialogDescription>
        </DialogHeader>
        {create.error ? (
          <Alert variant="destructive">
            <AlertTitle>{t('efs.firstValuation.create.error_title')}</AlertTitle>
            <AlertDescription>{create.error.message}</AlertDescription>
          </Alert>
        ) : null}
        <div className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-sm font-medium">{t('efs.firstValuation.fields.mode')}</label>
            {/* Kept as a dropdown with the single creatable mode on purpose,
                so the dialog looks the same while users get used to it. */}
            <Select value={mode} onValueChange={(v) => setMode(v as typeof mode)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {creatableFirstValuationModes.map((m) => (
                  <SelectItem key={m} value={m}>
                    {t(`efs.firstValuation.modes.${m}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={create.isPending}
          >
            {t('efs.common.actions.cancel', { defaultValue: 'キャンセル' })}
          </Button>
          <Button type="button" onClick={onConfirm} disabled={create.isPending}>
            {create.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
            {t('efs.firstValuation.actions.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ---- one company's valuation (blind isolation is visible here) -------------

const EstimatorRow = ({ submission, isOwn }: { submission: SubmissionDto; isOwn: boolean }) => {
  const { t } = useTranslation(NS);
  const isSubmitted = submission.is_submitted;
  // The server withholds the amount unless the viewer may see it, so a null
  // amount on a filed row IS the blind state — never guess at it here.
  const amount = submission.valuation_amount;

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-efs-border px-3 py-2 last:border-b-0">
      <div className="flex min-w-0 items-center gap-2">
        {isSubmitted ? (
          <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-900" />
        ) : (
          <Clock className="h-4 w-4 shrink-0 text-efs-text-muted" />
        )}
        <span className="truncate text-sm font-medium">{estimatorName(submission, t)}</span>
        {submission.estimator_role ? (
          <Badge variant="outline" className="shrink-0">
            {t(`efs.firstValuation.roles.${submission.estimator_role}`)}
          </Badge>
        ) : null}
        {isOwn ? (
          <Badge variant="secondary" className="shrink-0">
            {t('efs.firstValuation.estimator.own_badge')}
          </Badge>
        ) : null}
      </div>
      <div className="flex items-center gap-3">
        <span className="text-xs text-efs-text-muted">
          {submission.submitted_at
            ? t('efs.firstValuation.estimator.submitted_at', {
                at: formatDateTime(submission.submitted_at),
              })
            : t('efs.firstValuation.estimator.awaiting_input')}
        </span>
        {amount != null ? (
          <span className="font-mono text-sm font-semibold tabular-nums">
            {formatAmount(amount)}
          </span>
        ) : (
          <span className="text-sm text-efs-text-muted">
            {isSubmitted
              ? t('efs.firstValuation.estimator.amount_hidden')
              : t('efs.firstValuation.estimator.not_submitted')}
          </span>
        )}
      </div>
    </div>
  );
};

const EstimatorList = ({
  dto,
  myCompanyId,
}: {
  dto: FirstValuationDto;
  myCompanyId: string | null | undefined;
}) => {
  const { t } = useTranslation(NS);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('efs.firstValuation.estimator.title')}</CardTitle>
        <CardDescription>{t('efs.firstValuation.estimator.subtitle')}</CardDescription>
      </CardHeader>
      <CardContent className="px-0 pb-0">
        {dto.submissions.length === 0 ? (
          <p className="px-6 pb-6 text-sm text-efs-text-muted">
            {t('efs.firstValuation.estimator.empty')}
          </p>
        ) : (
          <div className="border-t border-efs-border">
            {dto.submissions.map((s) => (
              <EstimatorRow
                key={s.estimator_company_id ?? s.estimator_side ?? 'unknown'}
                submission={s}
                isOwn={!!myCompanyId && s.estimator_company_id === myCompanyId}
              />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// ---- comparison zone -------------------------------------------------------

const ComparisonZone = ({
  dto,
  canFinalize,
  onFinalize,
  onRequestRevaluation,
  revaluationPending,
}: {
  dto: FirstValuationDto;
  canFinalize: boolean;
  onFinalize: () => void;
  onRequestRevaluation: () => void;
  revaluationPending: boolean;
}) => {
  const { t } = useTranslation(NS);
  const comparison = dto.comparison;
  if (!comparison) return null;
  const band = comparison.difference_band;
  // The processor's number is the one a platform operator usually wants in
  // hand before confirming — flag its absence, but never block on it.
  const processorFiled = dto.submissions.some(
    (s) => s.estimator_role === 'processor' && s.is_submitted,
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('efs.firstValuation.comparison.title')}</CardTitle>
        <CardDescription>{t('efs.firstValuation.comparison.subtitle')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          <div>
            <p className="text-xs text-efs-text-muted">
              {t('efs.firstValuation.comparison.submitted_count')}
            </p>
            <p className="font-mono text-lg font-semibold tabular-nums">
              {comparison.submitted_count}
            </p>
          </div>
          <div>
            <p className="text-xs text-efs-text-muted">
              {t('efs.firstValuation.comparison.min_amount')}
            </p>
            <p className="font-mono text-lg font-semibold tabular-nums">
              {formatAmount(comparison.min_amount)}
            </p>
          </div>
          <div>
            <p className="text-xs text-efs-text-muted">
              {t('efs.firstValuation.comparison.max_amount')}
            </p>
            <p className="font-mono text-lg font-semibold tabular-nums">
              {formatAmount(comparison.max_amount)}
            </p>
          </div>
          <div>
            <p className="text-xs text-efs-text-muted">
              {t('efs.firstValuation.fields.middle_value')}
            </p>
            <p className="font-mono text-lg font-semibold tabular-nums">
              {formatAmount(comparison.middle_value)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-efs-text-muted">
            {t('efs.firstValuation.fields.difference_rate')}
          </span>
          <span className="font-mono text-sm tabular-nums">
            {formatRate(comparison.difference_rate)}
          </span>
          {band ? (
            <Badge variant={bandVariant(band)}>{t(`efs.firstValuation.bands.${band}`)}</Badge>
          ) : null}
        </div>
        {canFinalize && !processorFiled ? (
          <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-950 dark:text-amber-100">
            {t('efs.firstValuation.comparison.processor_missing')}
          </p>
        ) : null}
        {canFinalize ? (
          <div className="flex flex-wrap gap-2">
            <Button type="button" onClick={onFinalize}>
              {t('efs.firstValuation.actions.finalize')}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={onRequestRevaluation}
              disabled={revaluationPending}
            >
              {revaluationPending ? (
                <Loader2 className="mr-1 h-4 w-4 animate-spin" />
              ) : (
                <RotateCcw className="mr-1 h-4 w-4" />
              )}
              {t('efs.firstValuation.actions.request_revaluation')}
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};

// ---- confirmed / released summary ------------------------------------------

const FinalizedSummary = ({
  dto,
  canLock,
  onLock,
  lockPending,
}: {
  dto: FirstValuationDto;
  canLock: boolean;
  onLock: () => void;
  lockPending: boolean;
}) => {
  const { t } = useTranslation(NS);
  const isReleased = dto.status === 'locked';
  const adopted = dto.submissions.find((s) => s.estimator_company_id === dto.final_company_id);

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-base">{t('efs.firstValuation.summary.title')}</CardTitle>
          <Badge variant={isReleased ? 'secondary' : 'default'}>
            {t(`efs.firstValuation.status.${dto.status}`)}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <dt className="text-efs-text-muted">{t('efs.firstValuation.fields.final_value')}</dt>
          <dd className="font-mono font-semibold tabular-nums">{formatAmount(dto.final_value)}</dd>
          <dt className="text-efs-text-muted">{t('efs.firstValuation.fields.final_basis')}</dt>
          <dd>{dto.final_basis ? t(`efs.firstValuation.basis.${dto.final_basis}`) : '—'}</dd>
          {adopted ? (
            <>
              <dt className="text-efs-text-muted">
                {t('efs.firstValuation.fields.adopted_company')}
              </dt>
              <dd>{estimatorName(adopted, t)}</dd>
            </>
          ) : null}
          {dto.middle_value ? (
            <>
              <dt className="text-efs-text-muted">{t('efs.firstValuation.fields.middle_value')}</dt>
              <dd className="font-mono tabular-nums">{formatAmount(dto.middle_value)}</dd>
            </>
          ) : null}
          {dto.finalized_at ? (
            <>
              <dt className="text-efs-text-muted">{t('efs.firstValuation.fields.finalized_at')}</dt>
              <dd>{formatDateTime(dto.finalized_at)}</dd>
            </>
          ) : null}
        </dl>
        {dto.final_reason ? (
          <div className="rounded-md bg-efs-bg-subtle px-3 py-2 text-sm">
            <p className="mb-1 text-xs text-efs-text-muted">
              {t('efs.firstValuation.fields.final_reason')}
            </p>
            {dto.final_reason}
          </div>
        ) : null}
        {isReleased ? (
          <p className="text-xs text-efs-text-muted">{t('efs.firstValuation.summary.released')}</p>
        ) : (
          <p className="text-xs text-efs-text-muted">
            {t('efs.firstValuation.summary.release_hint')}
          </p>
        )}
        {!isReleased && canLock ? (
          <Button type="button" onClick={onLock} disabled={lockPending}>
            {lockPending ? (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" />
            ) : (
              <Lock className="mr-1 h-4 w-4" />
            )}
            {t('efs.firstValuation.actions.lock')}
          </Button>
        ) : null}
      </CardContent>
    </Card>
  );
};

// ---- detail body -----------------------------------------------------------

const ValuationDetail = ({ valuationId }: { valuationId: string }) => {
  const { t } = useTranslation(NS);
  const user = useCurrentUser();
  const detail = useFirstValuationDetail(valuationId);
  const lock = useLockFirstValuation();
  const revaluation = useRequestRevaluation();

  const [submitOpen, setSubmitOpen] = useState(false);
  const [finalizeOpen, setFinalizeOpen] = useState(false);

  // One company, one valuation: whoever holds the permission estimates for
  // their own company. The legacy per-side codes still count so accounts keep
  // working before an admin grants `estimation.submit`.
  const canSubmit =
    hasPermission(user, 'estimation.submit') ||
    hasPermission(user, 'estimation.ef.submit') ||
    hasPermission(user, 'estimation.dk.submit');
  const canFinalize = hasPermission(user, 'estimation.final_decide');
  const canLock = hasPermission(user, 'estimation.lock');
  const myCompanyId = user?.companyId ?? null;

  const dto = detail.data;

  if (detail.isLoading) {
    return <p className="text-sm text-efs-text-muted">{t('efs.common.messages.loading')}</p>;
  }
  if (!dto) {
    return <p className="text-sm text-efs-text-muted">{t('efs.common.messages.empty')}</p>;
  }

  const mine = ownSubmission(dto, myCompanyId);
  const isConfirmed = dto.status === 'finalized' || dto.status === 'locked';
  const editable =
    dto.status === 'open' ||
    dto.status === 'both_submitted' ||
    dto.status === 'revaluation_requested';

  return (
    <div className="space-y-4">
      {dto.status === 'revaluation_requested' ? (
        <Alert>
          <AlertTitle>{t('efs.firstValuation.revaluation.title')}</AlertTitle>
          <AlertDescription>{t('efs.firstValuation.revaluation.description')}</AlertDescription>
        </Alert>
      ) : null}

      {/* Price the goods that actually arrived, with their photos in view. The
          company's filed total is the sum of these rows (computed server-side),
          so the estimator never types a blind whole-batch figure. Only the
          caller's own company is ever loaded here. */}
      {editable && canSubmit ? (
        <ValuationPackageTable valuationId={valuationId} readOnly={mine?.is_submitted ?? false} />
      ) : null}

      {editable && canSubmit ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-efs-border bg-efs-bg-panel px-4 py-3">
          <div className="text-sm">
            <p className="font-medium">{t('efs.firstValuation.estimator.mine')}</p>
            <p className="text-xs text-efs-text-muted">
              {mine?.is_submitted
                ? t('efs.firstValuation.estimator.submitted')
                : t('efs.firstValuation.estimator.not_submitted')}
            </p>
          </div>
          <Button type="button" variant="outline" size="sm" onClick={() => setSubmitOpen(true)}>
            {mine?.is_submitted
              ? t('efs.firstValuation.actions.resubmit')
              : t('efs.firstValuation.actions.submit')}
          </Button>
        </div>
      ) : null}

      <EstimatorList dto={dto} myCompanyId={myCompanyId} />

      {dto.comparison && !isConfirmed ? (
        <ComparisonZone
          dto={dto}
          canFinalize={canFinalize}
          onFinalize={() => setFinalizeOpen(true)}
          onRequestRevaluation={() => revaluation.mutate(valuationId)}
          revaluationPending={revaluation.isPending}
        />
      ) : null}

      {isConfirmed ? (
        <FinalizedSummary
          dto={dto}
          canLock={canLock}
          onLock={() => lock.mutate(valuationId)}
          lockPending={lock.isPending}
        />
      ) : null}

      <SubmitDialog valuationId={valuationId} open={submitOpen} onOpenChange={setSubmitOpen} />

      {dto.comparison ? (
        <FinalizeDialog dto={dto} open={finalizeOpen} onOpenChange={setFinalizeOpen} />
      ) : null}
    </div>
  );
};

// ---- page ------------------------------------------------------------------

export const FirstValuationPage = () => {
  const { t } = useTranslation(NS);
  const { batchId } = useParams();
  const user = useCurrentUser();
  const list = useFirstValuationList(batchId);

  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const rows = list.data ?? [];
  const canCreate =
    hasPermission(user, 'estimation.submit') ||
    hasPermission(user, 'estimation.ef.input') ||
    hasPermission(user, 'estimation.dk.input');

  // Default the selection to the first valuation once loaded.
  const activeId = selectedId ?? rows[0]?.first_valuation_id ?? null;

  return (
    <div className="space-y-4">
      <PageHeader
        title={t('efs.firstValuation.title')}
        subtitle={t('efs.firstValuation.subtitle')}
        count={rows.length}
        actions={
          batchId && canCreate ? (
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="mr-1 h-4 w-4" />
              {t('efs.firstValuation.actions.create')}
            </Button>
          ) : null
        }
      />

      {rows.length === 0 ? (
        <div className="rounded-md border border-efs-border bg-efs-bg-panel px-4 py-8 text-center text-sm text-efs-text-muted">
          {t('efs.firstValuation.messages.empty')}
        </div>
      ) : (
        <>
          {rows.length > 1 ? (
            <div className="flex flex-wrap gap-2">
              {rows.map((row) => (
                <button
                  key={row.first_valuation_id}
                  type="button"
                  onClick={() => setSelectedId(row.first_valuation_id)}
                  className={cn(
                    'rounded-md border px-3 py-1.5 text-sm',
                    row.first_valuation_id === activeId
                      ? 'border-efs-accent bg-efs-bg-subtle text-efs-text-primary'
                      : 'border-efs-border text-efs-text-muted hover:text-efs-text-primary',
                  )}
                >
                  <span className="font-medium">
                    {row.valuation_code ?? t(`efs.firstValuation.modes.${row.mode}`)}
                  </span>
                  <span className="ml-2 text-xs">
                    {t(`efs.firstValuation.status.${row.status}`)}
                  </span>
                </button>
              ))}
            </div>
          ) : null}

          {activeId ? <ValuationDetail valuationId={activeId} /> : null}
        </>
      )}

      {batchId ? (
        <CreateDialog batchId={batchId} open={createOpen} onOpenChange={setCreateOpen} />
      ) : null}
    </div>
  );
};

export default FirstValuationPage;
